import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { getAccountId } from '../../../config/AppSettings';
import { CreateWorkflowRequest, CreateWorkflowResponse, WorkflowType } from '../../../listoramaAdmin-api/generated-src';
import ListORamaAdminApiFactory from '../../../listoramaAdmin-api/ListORamaAdminApiFactory';
import { validateInputId, validateSeedListItemsRow } from '../../../utils/validation';
import { BulkWorkflowFormView } from './BulkWorkflowFormView';
import { InputType } from './types';

export interface BulkUpdateUserStatusFormInput {
    inputFile?: File;
    workflowType?: WorkflowType;
    workflowContext?: string;
    inputType?: InputType;
    dryRun?: boolean;
}

interface BulkUpdateUserStatusResponseData {
    fetching?: boolean;
    error?: AxiosError;
}

export const buildWorkflowRequestInput = (inputData: BulkUpdateUserStatusFormInput): CreateWorkflowRequest => {
    const userAlias = window.sessionStorage.getItem('userAlias');
    if (!userAlias) {
        throw new Error('User alias not found! Please refresh page') as AxiosError;
    }
    if (!inputData.workflowType || !Object.values(WorkflowType).includes(inputData.workflowType as WorkflowType)) {
        throw new Error('Please ensure you have selected a workflow') as AxiosError;
    }
    if (inputData.workflowType === WorkflowType.DataDeletion && !inputData.inputType) {
        throw new Error('Please ensure you have selected an input type for data deletion workflow') as AxiosError;
    }
    return {
        workflowType: inputData.workflowType,
        operator: userAlias,
        workflowContext: inputData.workflowContext!,
        dryRun: inputData.dryRun || false
    };
};

export async function getWorkflowInfo(inputData: BulkUpdateUserStatusFormInput): Promise<CreateWorkflowResponse> {
    const workflowInput = buildWorkflowRequestInput(inputData);
    return ListORamaAdminApiFactory()
        .createWorkflow.createWorkflow(workflowInput)
        .then((response) => response.data);
}

interface UploadFileToS3Props {
    url: string;
    file: File;
    workflowcontext: string;
    workflowtype: string;
    workflowid: string;
    dryrun: string;
}

const METADATA_HEADER_PREFIX = 'x-amz-meta-';
const REQUEST_HEADERS = {
    EXPECTED_BUCKET_OWNER: 'x-amz-expected-bucket-owner',
    OPERATOR: METADATA_HEADER_PREFIX + 'operator',
    WORKFLOW_TYPE: METADATA_HEADER_PREFIX + 'workflow-type',
    WORKFLOW_CONTEXT: METADATA_HEADER_PREFIX + 'workflow-context',
    WORKFLOW_ID: METADATA_HEADER_PREFIX + 'workflow-id',
    DRY_RUN: METADATA_HEADER_PREFIX + 'dry-run'
};

export async function uploadFileToS3({
    file,
    url,
    workflowcontext,
    workflowid,
    workflowtype,
    dryrun
}: UploadFileToS3Props) {
    const accountId = getAccountId(); // Add accountId to /localSettings/settings.json if you want to use dev account

    /**
     * Metadata headers are used to store information about the workflow.
     * Metadata header values must match with the values used when created presigned URL.
     */
    const headers = new Headers({
        'Content-Type': 'text/plain',
        [REQUEST_HEADERS.EXPECTED_BUCKET_OWNER]: accountId,
        [REQUEST_HEADERS.OPERATOR]: window.sessionStorage.getItem('userAlias') || '',
        [REQUEST_HEADERS.WORKFLOW_TYPE]: workflowtype,
        [REQUEST_HEADERS.WORKFLOW_CONTEXT]: workflowcontext,
        [REQUEST_HEADERS.WORKFLOW_ID]: workflowid,
        [REQUEST_HEADERS.DRY_RUN]: dryrun
    });
    const response = await fetch(url, {
        method: 'PUT',
        headers: headers,
        body: file
    });
    if (!response.ok) {
        throw new Error('Error uploading file to S3');
    }
}

export const BulkWorkflowForm: React.FC = () => {
    const [responseData, setResponseData] = useState<BulkUpdateUserStatusResponseData>({ fetching: false });
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const navigate = useNavigate();

    const validateInputLine = (line: string, workflowType: WorkflowType, inputType?: InputType): boolean => {
        switch (workflowType) {
            case WorkflowType.DataDeletion:
                if (!inputType) {
                    throw new Error('Input type is required for data deletion workflow validation');
                }
                return validateInputId(line, inputType);
            case WorkflowType.UpdateAlternateOrdering:
                return validateInputId(line, InputType.LIST_ID);
            case WorkflowType.SyncFollowLists:
                return validateInputId(line, InputType.USER_ID);
            case WorkflowType.ListSpamDetection:
                return validateInputId(line, InputType.LIST_ID);
            case WorkflowType.ItemDescriptionSpamDetection:
                return validateInputId(line, InputType.LIST_ID);
            case WorkflowType.SeedListItems:
                return validateSeedListItemsRow(line);
            default:
                throw new Error(
                    `Unsupported WorkflowType selected: ${workflowType}, please update validation logic for this case`
                );
        }
    };

    /**
     * Confirms the input file and workflow types are validly formed.
     */
    const validateInput = (inputData: BulkUpdateUserStatusFormInput) => {
        if (!inputData?.inputFile || inputData.inputFile.size === 0) {
            setResponseData({ error: new Error('Please ensure you have entered all inputs.') as AxiosError });
            return;
        }

        if (!inputData?.workflowType || !Object.values(WorkflowType).includes(inputData.workflowType as WorkflowType)) {
            setResponseData({ error: new Error('Please ensure you have selected a workflow.') as AxiosError });
            return;
        }

        if (inputData.workflowType === WorkflowType.DataDeletion && !inputData?.inputType) {
            setResponseData({
                error: new Error(
                    'Please ensure you have selected an input type for data deletion workflow.'
                ) as AxiosError
            });
            return;
        }

        inputData.inputFile.text().then((fileText) => {
            setIsModalOpen(true);
            let firstLine = true;
            for (const line of fileText.split(/[\r\n]+/)) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                if (!firstLine && !validateInputLine(line, inputData.workflowType!, inputData.inputType)) {
                    setResponseData({
                        error: new Error('Please validate input is properly formatted.') as AxiosError
                    });
                    setIsModalOpen(false);
                    return;
                }
                firstLine = false;
            }
            setResponseData({ error: undefined });
            setIsModalOpen(true);
        });
    };

    const onInputChange = () => {
        // Clear any errors shown on page from invalid input
        setResponseData({ error: undefined });
    };

    const uploadInput = async (inputData: BulkUpdateUserStatusFormInput) => {
        if (!inputData?.inputFile) {
            setIsModalOpen(false);
            return;
        }

        setIsModalOpen(false);
        let workflowInfo = {} as CreateWorkflowResponse;
        try {
            setResponseData({ fetching: true });
            workflowInfo = await getWorkflowInfo(inputData);
        } catch (error) {
            setResponseData({ error: new Error(`Error creating S3 path!\n - ${error}`) as AxiosError });
            return;
        }
        setResponseData({ fetching: false });

        try {
            setResponseData({ fetching: true });

            if (!workflowInfo.uploadURL) {
                setResponseData({ error: new Error('No presigned URL created to upload!') as AxiosError });
                return;
            }

            // Input validation already checked above.
            await uploadFileToS3({
                url: workflowInfo.uploadURL,
                file: inputData.inputFile,
                workflowcontext: inputData.workflowContext!,
                workflowid: workflowInfo.workflowId!,
                workflowtype: inputData.workflowType!,
                dryrun: String(inputData.dryRun || false)
            });
        } catch (error) {
            setResponseData({ error: new Error('Error uploading file to S3') as AxiosError });
            return;
        }
        setResponseData({ fetching: false });

        navigate('/bulkWorkflow', {
            state: {
                successMessage: 'Bulk workflow submitted successfully! This workflow will be processed asynchronously.'
            }
        });

        return;
    };

    return (
        <BulkWorkflowFormView
            onInputChange={onInputChange}
            responseData={responseData}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            uploadInput={uploadInput}
            validateInput={validateInput}
        />
    );
};
